(() => {
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const colorFilterPrefixes = [
    'Details-filter.v.t.shopify.color-pattern',
    'Details-filter.p.m.custom.exact_color_filter',
  ];
  const ratingOrder = new Map([
    ['5 stars', 0],
    ['4 stars & up', 1],
    ['3 stars & up', 2],
    ['2 stars & up', 3],
    ['1 star & up', 4],
  ]);

  const syncMobileQuickColorsFromUrl = (root = document) => {
    const colorParam = document.querySelector('input[name="filter.p.m.custom.exact_color_filter"]')
      ? 'filter.p.m.custom.exact_color_filter'
      : 'filter.v.t.shopify.color-pattern';
    const currentUrl = new URL(window.location.href);
    const selectedColors = new Set(currentUrl.searchParams.getAll(colorParam));

    root.querySelectorAll('.custom-mobile-quick-colors__item').forEach((item) => {
      const filterValue = item.dataset.filterValue;
      const isActive = Boolean(filterValue && selectedColors.has(filterValue));
      // Quick links sit outside Dawn's AJAX-updated facets. Rebuild from the
      // applied URL so choosing a color never discards other selected filters.
      const nextUrl = new URL(currentUrl);
      nextUrl.searchParams.delete('page');
      nextUrl.searchParams.delete(colorParam);
      selectedColors.forEach((color) => {
        if (color !== filterValue) nextUrl.searchParams.append(colorParam, color);
      });
      if (!isActive && filterValue) nextUrl.searchParams.append(colorParam, filterValue);
      item.href = nextUrl.href;
      item.classList.toggle('is-active', isActive);
      if (isActive) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
  };

  const syncDesktopColorsFromUrl = (root = document) => {
    const selectedColors = new Set(
      new URLSearchParams(window.location.search).getAll('filter.p.m.custom.exact_color_filter'),
    );

    root
      .querySelectorAll('#FacetFiltersForm input[name="filter.p.m.custom.exact_color_filter"]')
      .forEach((input) => {
        input.checked = selectedColors.has(input.value);
      });
  };

  const prepareMobileApply = (root = document) => {
    root.querySelectorAll('#FacetFiltersFormMobile').forEach((form) => {
      if (form.dataset.customDeferredApplyReady === 'true') return;
      form.dataset.customDeferredApplyReady = 'true';

      form.addEventListener(
        'input',
        (event) => {
          // Sorting is a single choice with an immediate result. Keep Dawn's
          // native submission for it; only filter edits wait for Apply.
          if (event.target.matches('select[name="sort_by"]')) return;
          event.stopPropagation();
        },
        true,
      );
    });
  };

  const reorderRatingOptions = (root = document) => {
    root.querySelectorAll('details[id*="filter.p.m.custom.customer_rating_filter"]').forEach((filter) => {
      filter.querySelectorAll('.facet-checkbox__text[aria-hidden="true"]').forEach((label) => {
        Array.from(label.childNodes).forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && /\(\d+\)/.test(node.textContent)) node.remove();
        });
      });

      filter.querySelectorAll('ul').forEach((list) => {
      const items = Array.from(list.children);
      const sortedItems = [...items].sort((left, right) => {
        const leftRank = ratingOrder.get(left.querySelector('input')?.value) ?? 99;
        const rightRank = ratingOrder.get(right.querySelector('input')?.value) ?? 99;
        return leftRank - rightRank;
      });

      if (items.some((item, index) => item !== sortedItems[index])) list.append(...sortedItems);
      });
    });
  };

  const positionActiveMobileQuickColor = (root = document) => {
    if (desktopQuery.matches) return;

    root.querySelectorAll('.custom-mobile-quick-colors__list').forEach((list) => {
      const activeItem = list.querySelector('.custom-mobile-quick-colors__item.is-active');
      if (!activeItem) return;
      list.scrollLeft = Math.max(0, activeItem.offsetLeft - (list.clientWidth - activeItem.offsetWidth) / 2);
    });
  };

  const prepareDesktopSizeRows = (root = document) => {
    root
      .querySelectorAll('#FacetFiltersForm details[id^="Details-filter.v.option.size"] .facets__list')
      .forEach((list) => {
        const items = Array.from(list.children);
        const filter = list.closest('details');
        const hasItems = items.length > 0;
        // The reference row spans about 800px. Wrap by available width, not
        // by a numeric size, so every collection uses the same visual length.
        list.removeAttribute('data-custom-size-row-break');
        list.toggleAttribute('data-custom-size-wrap', hasItems);
        filter.toggleAttribute('data-custom-size-row-filter', hasItems);
        items.forEach((item) => item.removeAttribute('data-custom-size-row'));
        filter.style.removeProperty('--custom-size-filter-gap');
        if (hasItems) {
          const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
          const width = Math.min(list.closest('form').clientWidth, rootSize * 80);
          filter.style.setProperty('--custom-size-filter-width', `${width}px`);
          filter.style.setProperty('--custom-size-filter-padding', '1rem');
          filter.style.setProperty('--custom-size-filter-item-end-space', '2rem');
          const label = items[0].querySelector('.facet-checkbox');
          const text = label?.querySelector('.facet-checkbox__text-label');
          if (label && text && label.getBoundingClientRect().height) {
            const padding = parseFloat(getComputedStyle(label).paddingTop);
            const lineSpace = label.getBoundingClientRect().height - padding * 2 - text.getBoundingClientRect().height;
            const normalRowGap = rootSize * 0.7;
            const rowGap = normalRowGap * 0.6;
            const compactPadding = Math.max(0, ((padding * 2 + normalRowGap + lineSpace) * 0.6 - rowGap - lineSpace) / 2);
            filter.style.setProperty('--custom-size-filter-padding', `${compactPadding}px`);
          }
        } else {
          filter.style.removeProperty('--custom-size-filter-width');
          filter.style.removeProperty('--custom-size-filter-padding');
          filter.style.removeProperty('--custom-size-filter-item-end-space');
        }
      });
  };

  const prepareFilters = (root = document) => {
    syncMobileQuickColorsFromUrl(root);
    reorderRatingOptions(root);
    prepareMobileApply(root);
    positionActiveMobileQuickColor(root);
    if (!desktopQuery.matches) return;
    prepareDesktopSizeRows(root);

    root.querySelectorAll('.facets__wrapper').forEach((wrapper) => {
      const filters = Array.from(wrapper.querySelectorAll(':scope > details.facets__disclosure'));
      const colorFilter = filters.find((filter) => colorFilterPrefixes.some((prefix) => filter.id.startsWith(prefix)));
      const fitOpenFilter = () => {
        const openFilter = filters.find((filter) => filter.open);
        const display = openFilter?.querySelector(':scope > .facets__display');
        wrapper.style.paddingBottom = display ? `${display.scrollHeight + 44}px` : '';
        if (display && openFilter.hasAttribute('data-custom-size-row-filter')) {
          const labels = Array.from(display.querySelectorAll('.facets__list .facet-checkbox__text-label'));
          const firstPhoto = document.querySelector('#product-grid .card__inner');
          if (labels.length && firstPhoto) {
            const lastBottom = Math.max(...labels.map((label) => label.getBoundingClientRect().bottom));
            const currentGap = firstPhoto.getBoundingClientRect().top - lastBottom;
            const normalPadding = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const compactPadding = parseFloat(getComputedStyle(labels[0].closest('.facet-checkbox')).paddingBottom);
            const originalGap = currentGap + normalPadding - compactPadding;
            const reserve = Math.max(0, 44 - (currentGap - originalGap * 0.7));
            if (currentGap > 0) wrapper.style.paddingBottom = `${display.scrollHeight + reserve}px`;
          }
        }
      };

      filters.forEach((filter) => {
        if (filter.dataset.customHorizontalFacetReady === 'true') return;
        filter.dataset.customHorizontalFacetReady = 'true';

        filter.addEventListener('toggle', () => {
          if (filter.open) {
            prepareDesktopSizeRows(root);
            filters.forEach((otherFilter) => {
              if (otherFilter !== filter) otherFilter.removeAttribute('open');
            });
          }
          requestAnimationFrame(fitOpenFilter);
        });
      });

      if (colorFilter && !filters.some((filter) => filter.open)) colorFilter.open = true;
      requestAnimationFrame(fitOpenFilter);
    });
  };

  document.addEventListener('DOMContentLoaded', () => prepareFilters());
  document.addEventListener('shopify:section:load', (event) => prepareFilters(event.target));
  document.addEventListener('shopify:section:reorder', () => prepareFilters());
  desktopQuery.addEventListener('change', () => prepareFilters());

  let sizeRowsResizeFrame = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(sizeRowsResizeFrame);
    sizeRowsResizeFrame = requestAnimationFrame(() => {
      if (desktopQuery.matches) prepareFilters();
    });
  });
  document.fonts.ready.then(() => {
    if (desktopQuery.matches) prepareFilters();
  });

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('.custom-mobile-quick-colors__item')) {
        // Also refresh at activation, including clicks before an AJAX render
        // finishes. Keep normal link, modifier-key and navigation behavior.
        syncMobileQuickColorsFromUrl();
        return;
      }
      const desktopClearAll = event.target.closest(
        '.facets-container[data-template="collection"] .active-facets-desktop .active-facets__button-remove',
      );
      if (desktopQuery.matches && desktopClearAll?.textContent.trim().toLowerCase() === 'remove all') {
        event.preventDefault();
        event.stopImmediatePropagation();
        document
          .querySelectorAll('#FacetFiltersForm input[name="filter.p.m.custom.exact_color_filter"]:checked')
          .forEach((input) => {
            input.checked = false;
          });
        window.location.replace(desktopClearAll.href);
        return;
      }

      const mobileClearAll = event.target.closest('#FacetFiltersFormMobile .mobile-facets__clear');
      if (mobileClearAll && mobileClearAll.textContent.trim().toLowerCase() === 'remove all') {
        event.preventDefault();
        event.stopImmediatePropagation();
        document.querySelectorAll('.custom-mobile-quick-colors__item.is-active').forEach((item) => {
          item.classList.remove('is-active');
          item.removeAttribute('aria-current');
        });
        window.location.replace(mobileClearAll.href);
        return;
      }

      const searchReset = event.target.closest(
        '.facets-container[data-template="search"] .active-facets__button-remove',
      );
      if (searchReset) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.assign(searchReset.href);
        return;
      }

      if (desktopQuery.matches) return;

      const applyButton = event.target.closest('.mobile-facets__footer .button--primary');
      const form = applyButton?.closest('facet-filters-form')?.querySelector('#FacetFiltersFormMobile');
      const facetFilters = form?.closest('facet-filters-form');
      if (!form || !facetFilters) return;

      const searchParams = facetFilters.createSearchParams(form);
      facetFilters.onSubmitForm(searchParams, event);
    },
    true,
  );

  window.addEventListener('pageshow', () => {
    syncMobileQuickColorsFromUrl();
    syncDesktopColorsFromUrl();
  });

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length)) prepareFilters();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
